// web/js/player/hooks/usePlaybackShuttle.ts
//
// Playback shuttle hook: owns rewind / fast-forward / slow-step state
// and commands that were previously private to useKeyboardNav. Exposed
// on KollabPlayerAPI so external code (sync, tests, future UI controls)
// can drive playback shuttling without going through keyboard events.
//
// Semantics are preserved verbatim from the original useKeyboardNav
// implementation. See docs/superpowers/specs/2026-04-08-playback-shuttle-api-design.md
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PlayerEventType } from '../types';
import type { PlayerEventCallback } from '../types';

export interface PlaybackShuttleOptions {
  /**
   * Ref object holding the media element the shuttle should drive.
   * We take a ref (not a value) because the caller typically assigns
   * its `mediaRef.current = el` inside a useEffect that runs AFTER the
   * first render commit. Reading a value at render time would capture
   * `null` and stick, since nothing guarantees a follow-up re-render
   * before the user interacts. By taking the ref object, every callback
   * inside the hook reads `mediaRef.current` at CALL time, always
   * getting the current video element.
   */
  mediaRef: React.RefObject<HTMLMediaElement | null>;
  fps: number;
  onSeek?: (time: number) => void;
  onShowStatus?: (message: string) => void;
  subscribersRef: React.RefObject<PlayerEventCallback[]>;
}

export interface PlaybackShuttle {
  rewind: () => void;
  fastForward: () => void;
  startSlowStepBack: () => void;
  startSlowStepForward: () => void;
  stopSlowStep: () => void;
  clearRewind: () => void;
  clearSlowStep: () => void;
  incrementFrames: (num: number) => void;
  /**
   * Central helper for changing the player's currentTime. All code that
   * needs to move the playhead MUST go through this so:
   *   1. `time` is clamped to [0, duration].
   *   2. `onSeek` callback fires with the clamped value.
   *   3. A `SEEK` event is emitted to subscribers — this is what keeps
   *      follower columns in sync when the source column scrubs.
   * Direct `mediaEl.currentTime = X` assignments silently desync followers.
   */
  seekTo: (time: number) => void;
}

export function usePlaybackShuttle(opts: PlaybackShuttleOptions): PlaybackShuttle {
  const { mediaRef, fps, onSeek, onShowStatus, subscribersRef } = opts;

  const rewindRateRef = useRef(0);
  const rewindIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const slowStepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const notify = useCallback(
    (type: PlayerEventType) => {
      const mediaEl = mediaRef.current;
      if (!mediaEl) return;
      for (const subscriber of subscribersRef.current) {
        subscriber({
          type,
          currentTime: mediaEl.currentTime,
          playbackRate: mediaEl.playbackRate,
          isPaused: mediaEl.paused,
        });
      }
    },
    [mediaRef, subscribersRef],
  );

  // Central seek helper. Every place that needs to move the playhead goes
  // through this so a SEEK event is always emitted to subscribers. Without
  // this, follower columns silently desync until the time_update drift
  // threshold kicks in and forces a catch-up (which causes spinners).
  const seekTo = useCallback(
    (time: number) => {
      const mediaEl = mediaRef.current;
      if (!mediaEl) return;
      const duration = mediaEl.duration;
      const max = Number.isFinite(duration) && duration > 0 ? duration : Number.POSITIVE_INFINITY;
      const clamped = Math.max(0, Math.min(max, time));
      mediaEl.currentTime = clamped;
      onSeek?.(clamped);
      notify(PlayerEventType.SEEK);
    },
    [mediaRef, onSeek, notify],
  );

  const clearRewind = useCallback(() => {
    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
      rewindIntervalRef.current = null;
    }
    rewindRateRef.current = 0;
    notify(PlayerEventType.CLEAR_REWIND);
  }, [notify]);

  const clearSlowStep = useCallback(() => {
    if (slowStepIntervalRef.current) {
      clearInterval(slowStepIntervalRef.current);
      slowStepIntervalRef.current = null;
    }
    notify(PlayerEventType.STOP_SLOW_STEP);
  }, [notify]);

  const fakeRewind = useCallback(() => {
    const mediaEl = mediaRef.current;
    if (!mediaEl) return;

    if (rewindRateRef.current < 2.0) {
      rewindRateRef.current += 0.25;
    }

    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
    }

    rewindIntervalRef.current = setInterval(() => {
      // Re-read inside the interval tick — mediaRef.current could in
      // principle change between schedule and tick.
      const el = mediaRef.current;
      if (!el) return;
      el.playbackRate = 1.0;

      if (el.currentTime <= 0.1) {
        el.pause();
        clearRewind();
        // Use seekTo so followers learn we snapped to zero.
        seekTo(0);
      } else {
        // Move currentTime back BEFORE calling play() - otherwise
        // play() on an ended video resets to the beginning. Routed
        // through seekTo so every tick emits a SEEK event for sync.
        seekTo(el.currentTime - rewindRateRef.current);
        if (el.paused) el.play();
      }
    }, 100);
  }, [mediaRef, clearRewind, seekTo]);

  const rewind = useCallback(() => {
    const mediaEl = mediaRef.current;
    if (!mediaEl) return;

    // If fast-forwarding and still playing, ramp the rate down.
    // Skip the ramp-down when ended - the high rate is meaningless
    // because nothing is playing.
    if (mediaEl.playbackRate > 1.0 && !mediaEl.ended) {
      mediaEl.playbackRate -= 2.0;
      if (mediaEl.playbackRate === 0) mediaEl.playbackRate = 1.0;
      onShowStatus?.(`${mediaEl.playbackRate}x`);
      return;
    }

    // Reset a stale high rate (e.g. after fast-forwarding to the end)
    if (mediaEl.playbackRate > 1.0) {
      mediaEl.playbackRate = 1.0;
    }

    // Try native reverse playback (Safari)
    let expectedRate = mediaEl.playbackRate || 1.0;

    try {
      if (expectedRate > -16.0) {
        if (expectedRate > 0 && expectedRate < 2.0) {
          expectedRate = -1.0;
        } else if (expectedRate >= -1.0) {
          expectedRate = -2.0;
        } else {
          expectedRate -= 2.0;
        }
      }

      mediaEl.playbackRate = expectedRate;

      if (expectedRate !== mediaEl.playbackRate) {
        fakeRewind();
        onShowStatus?.(`-${rewindRateRef.current * 4}x`);
      } else {
        mediaEl.play();
        onShowStatus?.(`${expectedRate}x`);
      }
    } catch {
      fakeRewind();
      onShowStatus?.(`-${rewindRateRef.current * 4}x`);
    }

    notify(PlayerEventType.REWIND);
  }, [mediaRef, fakeRewind, onShowStatus, notify]);

  const fastForward = useCallback(() => {
    const mediaEl = mediaRef.current;
    if (!mediaEl) return;

    // Already at the end - do nothing.
    if (mediaEl.ended || (mediaEl.duration && mediaEl.currentTime >= mediaEl.duration)) return;

    if (rewindRateRef.current > 0) {
      rewindRateRef.current -= 0.5;
      return;
    }

    if (mediaEl.paused) {
      mediaEl.playbackRate = 1.0;
      mediaEl.play();
      onShowStatus?.(`${mediaEl.playbackRate}x`);
      return;
    }

    clearRewind();
    mediaEl.pause();

    if (mediaEl.playbackRate < 16.0) {
      if (mediaEl.playbackRate + 2.0 === 0) {
        mediaEl.playbackRate = 1.0;
      } else if (mediaEl.playbackRate < 2.0) {
        mediaEl.playbackRate += 0.5;
      } else {
        mediaEl.playbackRate += 2.0;
      }

      notify(PlayerEventType.FAST_FORWARD);
    }

    if (mediaEl.playbackRate === 0) mediaEl.playbackRate = 1.0;
    mediaEl.play();
    onShowStatus?.(`${mediaEl.playbackRate}x`);
  }, [mediaRef, clearRewind, onShowStatus, notify]);

  const incrementFrames = useCallback(
    (num: number) => {
      const mediaEl = mediaRef.current;
      if (!mediaEl) return;
      mediaEl.pause();
      // Compute the target time, then route through seekTo so the SEEK
      // event (and onSeek callback) fire via the single central path.
      const target = fps > 0
        // Convert to integer frame number, increment, convert back.
        // This avoids floating-point drift from repeated 1/fps additions.
        ? (Math.round(mediaEl.currentTime * fps) + num) / fps
        : mediaEl.currentTime + num;
      seekTo(target);
    },
    [mediaRef, fps, seekTo],
  );

  // Internal helper so the two public start methods share the interval setup.
  const startSlowStep = useCallback(
    (direction: 1 | -1) => {
      if (slowStepIntervalRef.current) return; // already shuttling
      incrementFrames(direction); // immediate first step
      slowStepIntervalRef.current = setInterval(() => {
        incrementFrames(direction);
      }, 200); // ~5 fps shuttle speed

      notify(
        direction === 1
          ? PlayerEventType.START_SLOW_STEP_FORWARD
          : PlayerEventType.START_SLOW_STEP_BACK,
      );
    },
    [incrementFrames, notify],
  );

  const startSlowStepBack = useCallback(() => startSlowStep(-1), [startSlowStep]);
  const startSlowStepForward = useCallback(() => startSlowStep(1), [startSlowStep]);
  const stopSlowStep = useCallback(() => clearSlowStep(), [clearSlowStep]);

  // Cleanup any running intervals on unmount.
  useEffect(() => {
    return () => {
      if (rewindIntervalRef.current) clearInterval(rewindIntervalRef.current);
      if (slowStepIntervalRef.current) clearInterval(slowStepIntervalRef.current);
    };
  }, []);

  // Memoize the return object so useImperativeHandle consumers get a stable
  // reference while the individual methods are stable via useCallback.
  return useMemo(
    () => ({
      rewind,
      fastForward,
      startSlowStepBack,
      startSlowStepForward,
      stopSlowStep,
      clearRewind,
      clearSlowStep,
      incrementFrames,
      seekTo,
    }),
    [rewind, fastForward, startSlowStepBack, startSlowStepForward, stopSlowStep, clearRewind, clearSlowStep, incrementFrames, seekTo],
  );
}
